// import { PHONE_NUMBER_REGEX } from 'src/constants';
import { PHONE_NUMBER_REGEX } from 'src/constants';
import * as yup from 'yup';

const commonScheme = {
  required: yup.string().required('schemaErrors.required'),
  name: yup.string().required('schemaErrors.required'),
  firstName: yup.string().required('schemaErrors.required'),
  lastName: yup.string().required('schemaErrors.required'),
  email: yup.string().email('schemaErrors.invalidEmail').required('schemaErrors.required'),
  password: yup
    .string()
    .min(6, 'schemaErrors.shortPass')
    .max(12, 'schemaErrors.longPass')
    .required('schemaErrors.required'),
  passwordConfirmation: yup
    .string()
    .min(6, 'schemaErrors.shortPass')
    .max(12, 'schemaErrors.longPass')
    .required('schemaErrors.required')
    .oneOf([yup.ref('password'), null], 'schemaErrors.confirmPass'),
  phoneNumber: yup.string().matches(PHONE_NUMBER_REGEX, 'schemaErrors.invalidPhone').required('schemaErrors.required'),
  number: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(1, 'schemaErrors.minValueError')
    .required('schemaErrors.required')
    .typeError('schemaErrors.numberError'),
};

export const signinFormScheme = yup.object().shape({
  email: commonScheme.email,
  // Sign-in must not apply the sign-up length rules. An existing account may hold a
  // password those rules would reject, and the server is the authority on whether a
  // password is correct. Enforcing 6-12 here only disables the submit button.
  password: commonScheme.required,
});
export const otp = yup.object().shape({
  otp: yup
    .string()
    .required('schemaErrors.required')
    .min(6, 'schemaErrors.otpLen')
    .max(6, 'schemaErrors.otpLen')
    .matches(/^.{6}$/, 'schemaErrors.otpLen'),
});
export const chatFormSchema = yup.object().shape({
  email: commonScheme.required,
});

export const privacyFormSchema = yup.object().shape({
  title: yup.object().shape({
    en: commonScheme.required,
    fr: commonScheme.required,
  }),
  description: yup.object().shape({
    en: commonScheme.required,
    fr: commonScheme.required,
  }),
});
export const joinFormSchema = yup.object().shape({
  firstName: commonScheme.firstName,
  lastName: commonScheme.lastName,
  email: commonScheme.required,
  phoneNumber: commonScheme.phoneNumber,
  password: commonScheme.password,
  confirmPassword: commonScheme.passwordConfirmation,
  gender: commonScheme.required.notOneOf(['default']),
  dob: yup
    .date()
    .required('schemaErrors.required')
    .max(new Date(), 'schemaErrors.DOBCheck')
    .min(new Date(new Date().setFullYear(new Date().getFullYear() - 100)), 'schemaErrors.invalidDOB'),
});
export const updateSettingSchema = yup.object().shape({
  cement: commonScheme.number,
  sand: commonScheme.number,
  gravel815: commonScheme.number,
  gravel1525: commonScheme.number,
  taxRate: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'schemaErrors.taxMinValueError')
    .max(100, 'schemaErrors.taxMaxValueError')
    .required('schemaErrors.required')
    .typeError('schemaErrors.numberError'),
  bankName: commonScheme.required,
  accountNumber: commonScheme.required,
  accountName: commonScheme.required,
});
export const addUser = yup.object().shape({
  email: commonScheme.required,
  role: commonScheme.required.notOneOf(['default']),
});
export const signUpSchema = yup.object().shape({
  email: commonScheme.email,
  firstName: commonScheme.firstName,
  lastName: commonScheme.lastName,
  phoneNumber: commonScheme.phoneNumber,
  streetAddress: commonScheme.required,
  streetNumber: commonScheme.required,
  reference: commonScheme.required,
  municipality: commonScheme.required,
  city: commonScheme.required,
  password: commonScheme.password,
  confirmPassword: commonScheme.passwordConfirmation,
});
export const resetPasswordSchema = yup.object().shape({
  currentPass: commonScheme.password,
  password: commonScheme.password,
  confirmPassword: commonScheme.passwordConfirmation,
});
export const forgetFormSchema = yup.object().shape({
  email: commonScheme.email,
});
export const newOrderSchema = yup.object().shape({
  volumeOfConcrete: commonScheme.number,
  dosage: commonScheme.required,
  deliveryDate: yup
    .date()
    .required('schemaErrors.required')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  timeOfDelivery: commonScheme.required.notOneOf(['default']),
  customerId: commonScheme.required,
  streetAddressOrder: commonScheme.required,
  streetNumberOrder: commonScheme.required,
  municipalityOrder: commonScheme.required,
  cityOrder: commonScheme.required,
});
export const manualOrderSchema = yup.object().shape({
  email: commonScheme.email,
  firstName: commonScheme.firstName,
  lastName: commonScheme.lastName,
  phoneNumber: commonScheme.phoneNumber,
  streetAddress: commonScheme.required,
  streetNumber: commonScheme.required,
  municipality: commonScheme.required,
  city: commonScheme.required,
  streetAddressOrder: commonScheme.required,
  streetNumberOrder: commonScheme.required,
  municipalityOrder: commonScheme.required,
  cityOrder: commonScheme.required,
  reference: commonScheme.required,
  role: commonScheme.required,
  volumeOfConcrete: commonScheme.number,
  dosage: commonScheme.required,
  deliveryDate: yup
    .date()
    .required('schemaErrors.required')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  timeOfDelivery: commonScheme.required.notOneOf(['default']),
});
export const updateOrderSchema = yup.object().shape({
  orderNumber: commonScheme.required,
  status: commonScheme.required,
  paymentMethod: yup.string().when('status', {
    is: (status) => ['PENDING_REVIEW', 'NEGOTIATION_FAILURE'].includes(status),
    then: () => yup.string().nullable(),
    otherwise: () => commonScheme.required.notOneOf(['default']),
  }),
  customerId: commonScheme.required,
  subOrder: yup
    .array()
    .of(
      yup.object().shape({
        timeOfDelivery: commonScheme.required,
        dosage: commonScheme.required,
        volumeOfConcrete: yup.number().required('This field is required'),
        perCubicMeterConcretePrice: yup.number().required('This field is required'),
        deliveryDate: commonScheme.required,
      })
    )
    .min(1, 'At least one subOrder is required'),
  phoneNumber: yup.string().when('paymentMethod', {
    is: (paymentMethod) => paymentMethod === 'UNIPESA',
    then: () => commonScheme.phoneNumber,
    otherwise: () => yup.string().nullable(),
  }),
  providerId: yup.string().when('paymentMethod', {
    is: (paymentMethod) => paymentMethod === 'UNIPESA',
    then: () => yup.string().required('Provider ID is required'),
    otherwise: () => yup.string().nullable(),
  }),
});
export const subOrderSchema = yup.object().shape({
  orderNumber: commonScheme.required,
  status: commonScheme.required,
  dosage: commonScheme.required,
  volumeOfConcrete: commonScheme.number,
  perCubicMeterConcretePrice: commonScheme.number,
  deliveryDate: commonScheme.required,
  streetAddress: commonScheme.required,
  streetNumber: commonScheme.required,
  municipality: commonScheme.required,
  city: commonScheme.required,
});
export const addInventorySchema = yup.object().shape({
  cement: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'schemaErrors.minValueError'),
  sand: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'schemaErrors.minValueError'),
  gravel815: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'schemaErrors.minValueError'),
  gravel1525: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'schemaErrors.minValueError'),
});
export const addInventorySchemaRequired = yup
  .object()
  .shape({
    cement: yup
      .number()
      .required()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(1, 'schemaErrors.minValueError'),
    sand: yup
      .number()
      .required()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(1, 'schemaErrors.minValueError'),
    gravel815: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(1, 'schemaErrors.minValueError')
      .nullable(), // Allow null if gravel1525 is provided
    gravel1525: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(1, 'schemaErrors.minValueError')
      .nullable(), // Allow null if gravel815 is provided
    concrete: yup
      .number()
      .required('Concrete is required')
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(1, 'schemaErrors.minValueError'),
  })
  .test('at-least-one-gravel', 'Either gravel815 or gravel1525 must be provided', function (values) {
    const { gravel815, gravel1525 } = values;
    return gravel815 >= 1 || gravel1525 >= 1;
  });

export const BankDetailsSchema = yup.object().shape({
  accountName: commonScheme.required,
  accountNumber: commonScheme.required,
  bankName: commonScheme.required,
});
